<template>
  <div class="patientTabs">
    <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
      <a-tab-pane :key="0" tab="进行中" />
      <a-tab-pane :key="1" tab="已完成" />
    </a-tabs>
    <a-spin :spinning="tabData.loading" wrapperClassName="tabContentWrapper">
      <div class="tabContent">
        <template v-if="tabData.list.length > 0">
          <div ref="patientListRef" class="tabListWrapper" @scroll="updateShadow">
            <div
              v-for="(patient, index) in tabData.list"
              :key="index"
              class="tabItem"
              @click="goPatientDetail(patient)"
            >
              <PatientCard
                :patient="patient"
                :patientDiagnosisList="patientDiagnosisList"
                :activeTab="activeTab"
              />
            </div>
            <!-- 阴影吸附在底部 -->
            <div v-show="showShadow" class="timeline-shadow"></div>
          </div>
          <a-pagination
            :pageSizeOptions="['10', '20', '30', '40']"
            show-size-changer
            show-quick-jumper
            v-model:current="pagination.curPage"
            v-model:pageSize="pagination.pageSize"
            :total="pagination.total"
            @change="onShowSizeChange"
            @showSizeChange="onShowSizeChange"
            :show-total="total => `共 ${pagination.total} 条`"
          />
        </template>
        <div class="emptyWrapper" v-else>
          <Empty :emptyImg="emptyImg" emptyText="暂无搜索结果" />
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script setup>
import { ref, reactive, defineExpose, defineProps, defineEmits, markRaw, nextTick } from 'vue'
import PatientCard from '../PatientCard'
import Empty from '../../../components/Empty'
import { patientDiagnosisList } from '../../constant'
import { getServicePatients } from '@/api/workPlace/serviceAttribution'
import emptyImg from '@/assets/img/empty.png'
import PatientService from '../../../PatientService'
const emit = defineEmits(['pushBreadcrumb'])
const activeTab = ref(0)
// 展示底部阴影
const showShadow = ref(true)
// 数据统一管理
const tabData = reactive({
  list: [],
  loading: false
})
const initPagination = {
  curPage: 1,
  pageSize: 10,
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
  showTotal: () => {}
}
const pagination = reactive({ ...initPagination })
// 执行患者搜索额外查询参数
const searchParams = reactive({})
const patientListRef = ref(null)
const props = defineProps({
  ucDoctorId: {
    type: String,
    default: ''
  },
  userId: {
    type: String,
    default: ''
  }
})
// 改变页数条数
const onShowSizeChange = (page, pageSize) => {
  pagination.curPage = page
  pagination.pageSize = pageSize
  getData()
}
// 获取指定页数据
const getData = async () => {
  try {
    tabData.loading = true
    const result = await fetchPatientListData({
      extraParams: searchParams
    })
    updateTabData(result)
  } catch (error) {
    handleError(error)
  } finally {
    tabData.loading = false
  }
}
// 获取服务患者列表
const fetchPatientListData = async ({ tabKey = activeTab.value, extraParams = {} }) => {
  const { curPage, pageSize } = pagination
  const params = {
    curPage,
    pageSize,
    ucDoctorId: props.ucDoctorId,
    completeStatus: Number(tabKey),
    ...extraParams
  }
  return await getServicePatients(params)
}
/**
 * 处理时间线底部阴影的显示逻辑
 * 1. 等待 DOM 更新完成（nextTick）
 * 2. 将列表滚动条重置到顶部（避免保留旧滚动位置）
 * 3. 重新计算阴影是否需要显示（updateShadow）
 */
const handleShadowVisible = () => {
  nextTick(() => {
    if (patientListRef.value) {
      patientListRef.value.scrollTop = 0
    }
    updateShadow()
  })
}
// 更新 tab 数据
const updateTabData = data => {
  tabData.list = data.dataList
  Object.assign(pagination, {
    total: data.totalSize
  })
  handleShadowVisible()
}
// 处理错误
const handleError = () => {
  tabData.list = []
}
// 初始化
const init = async () => {
  try {
    tabData.loading = true
    const result = await fetchPatientListData({
      extraParams: searchParams
    })
    updateTabData(result)
  } catch (error) {
    handleError(error)
  } finally {
    tabData.loading = false
  }
}
// 搜索
const onSearch = async keywordParams => {
  Object.assign(searchParams, keywordParams)
  try {
    tabData.loading = true
    pagination.curPage = 1
    // 获取当前tab数据
    const firstResult = await fetchPatientListData({
      extraParams: keywordParams
    })
    if (firstResult?.dataList?.length > 0) {
      updateTabData(firstResult)
    } else {
      // 若当前tab没有数据，则获取另外一个tab数据
      const toggleTab = activeTab.value === 0 ? 1 : 0
      const secondResult = await fetchPatientListData({
        tabKey: toggleTab,
        extraParams: keywordParams
      })
      if (secondResult?.dataList?.length > 0) {
        updateTabData(secondResult)
        activeTab.value = toggleTab
      } else {
        tabData.list = []
      }
    }
    if (patientListRef.value) {
      patientListRef.value.scrollTop = 0
    }
  } catch (error) {
    handleError(error)
  } finally {
    tabData.loading = false
  }
}
// 切换 tab
const onTabChange = async tabKey => {
  try {
    tabData.loading = true
    pagination.curPage = 1
    const result = await fetchPatientListData({
      tabKey,
      extraParams: searchParams
    })
    updateTabData(result)
  } catch (error) {
    handleError(error)
  } finally {
    tabData.loading = false
  }
}
// 重置
const resetTabData = () => {
  tabData.list = []
  Object.assign(pagination, initPagination)
  Object.keys(searchParams).forEach(k => delete searchParams[k])
  activeTab.value = 0
}
// 跳转至服务患者
const goPatientDetail = patient => {
  const { ucDoctorId, userId } = props
  const breadcrumbStack = {
    title: '服务患者',
    component: markRaw(PatientService),
    props: {
      ucDoctorId,
      userId,
      ucPatientId: patient.ucPatientId
    }
  }
  emit('pushBreadcrumb', breadcrumbStack)
}
// 监听服务患者滚动
const updateShadow = () => {
  if (!patientListRef.value) return
  const bottomDistance =
    patientListRef.value.scrollHeight -
    patientListRef.value.scrollTop -
    patientListRef.value.clientHeight
  showShadow.value = bottomDistance < 10 ? false : true
}
// 暴露方法
defineExpose({
  onSearch,
  resetTabData,
  init
})
</script>

<style scoped lang="scss">
.patientTabs {
  height: 100%;
  display: flex;
  flex-direction: column;
  .tabContentWrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;

    ::v-deep(.ant-spin-container) {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .tabContent {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;

      .tabListWrapper {
        flex: 1;
        min-height: 0;
        overflow: auto;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        align-content: flex-start;
        margin-bottom: 16px;
        position: relative;
        .tabItem {
          width: 25%;
          min-width: 300px;
        }
        .timeline-shadow {
          position: sticky;
          bottom: 0;
          width: 100%;
          height: 8px;
          pointer-events: none;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.05), transparent);
        }
      }

      .emptyWrapper {
        width: 100%;
        height: 80%;
      }
    }
  }
}
</style>
